import React, { startTransition } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadIcon from "@mui/icons-material/Upload";
import ScienceIcon from "@mui/icons-material/Science";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { useNavigate } from "react-router-dom";

import { App, Variant } from "../../types/app";
import { Project } from "../../types/project";
import { TestBuild } from "../../types/testBuild";
import projectService from "../../features/projects/services/projectService";
import variantService from "../../features/projects/services/variantService";
import uploadService from "../../features/uploads/services/uploadService";
import testBuildService from "../../features/testBuilds/services/testBuildService";
import useAuth from "../../features/auth/hooks/useAuth";
import { getApiErrorMessage } from "../../lib/api-client";
import { replaceKey } from "../../lib/appVersions";
import { emitUploadComplete } from "../../lib/uploadEvents";
import { UploadMode, UploadRequest } from "../../features/globalUpload/types";
import TestBuildUploadForm from "../TestBuildUploadForm";
// @ts-ignore - plain JS form, untyped
import AppForm from "../../forms/App/index";

/** Human-readable file size, e.g. "12.4 MB". */
const formatSize = (bytes: number): string => {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export interface UploadSlideOverProps {
  open: boolean;
  request: UploadRequest;
  onClose: () => void;
}

/**
 * The single upload surface: a right-hand slide-over hosted by
 * GlobalUploadProvider. Every upload path (top bar, Uploads nav, drag-drop,
 * Upload build, Add version, Edit, Test build) opens it with an UploadRequest.
 * It fetches its own projects, variants and existing builds, so callers only
 * say what they want to upload.
 */
const UploadSlideOver: React.FC<UploadSlideOverProps> = ({
  open,
  request,
  onClose,
}) => {
  const navigate = useNavigate();
  const { profile } = useAuth();

  const [projects, setProjects] = React.useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = React.useState(false);
  const [selectedProjectId, setSelectedProjectId] = React.useState<
    string | number | ""
  >(request.projectId ?? "");
  const [variants, setVariants] = React.useState<Variant[]>([]);
  const [variantsLoading, setVariantsLoading] = React.useState(
    Boolean(request.projectId)
  );
  const [uploadType, setUploadType] = React.useState<UploadMode | null>(
    request.mode ?? null
  );
  const [existingApps, setExistingApps] = React.useState<App[]>([]);
  const [existingTestBuilds, setExistingTestBuilds] = React.useState<TestBuild[]>(
    []
  );
  const [uploadProgress, setUploadProgress] = React.useState<number | null>(null);
  const [testBuildUploading, setTestBuildUploading] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const busy =
    (uploadType === "app" && uploadProgress !== null) || testBuildUploading;
  const canManageVariants = profile?.role !== "user";
  const replaceKeys = existingApps.map((a) => replaceKey(a));
  const showChooser = !selectedProjectId || !uploadType;

  React.useEffect(() => {
    if (!open) return;
    setProjectsLoading(true);
    projectService
      .getProjects()
      .then((data) => {
        const list = Array.isArray(data) ? data : Object.values(data || {});
        setProjects(list as Project[]);
      })
      .catch(() => setProjects([]))
      .finally(() => setProjectsLoading(false));
  }, [open]);

  const fetchVariants = React.useCallback(() => {
    if (!selectedProjectId) {
      setVariants([]);
      return;
    }
    setVariantsLoading(true);
    variantService
      .getVariants(selectedProjectId)
      .then(setVariants)
      .catch(() => setVariants([]))
      .finally(() => setVariantsLoading(false));
  }, [selectedProjectId]);

  React.useEffect(() => {
    fetchVariants();
  }, [fetchVariants]);

  // Existing builds feed the replace warning (app) and the iteration preview
  // (test build), so they're loaded on every path rather than passed in.
  React.useEffect(() => {
    if (!selectedProjectId) return;
    if (uploadType === "app" && !request.editApp) {
      uploadService
        .getAppsByProject(selectedProjectId)
        .then((data) => setExistingApps(Array.isArray(data) ? data : []))
        .catch(() => setExistingApps([]));
    } else if (uploadType === "testBuild") {
      testBuildService
        .listBuilds(selectedProjectId)
        .then(setExistingTestBuilds)
        .catch(() => setExistingTestBuilds([]));
    }
  }, [uploadType, selectedProjectId, request.editApp]);

  const selectedProject = projects.find(
    (p) => String(p.id) === String(selectedProjectId)
  );

  const handleCreateApp = async (formData: any) => {
    try {
      const result = await uploadService.createApp({
        ...formData,
        projectId: selectedProjectId,
      });
      // A build whose variant + version name + version code matched an existing
      // one overwrites it and returns 200, not an error. Say which happened.
      const replaced = (result as { replaced?: boolean })?.replaced === true;
      setSnackbar({
        open: true,
        message: replaced
          ? "Build replaced — the previous build and its file were deleted"
          : "App uploaded successfully",
        severity: "success",
      });
      setUploadProgress(null);
      emitUploadComplete({ projectId: selectedProjectId, type: "app" });
      onClose();
    } catch (err) {
      setUploadProgress(null);
      const message = getApiErrorMessage(err, "Failed to upload app");
      // "Variant not found" / "does not belong to this project" mean the picker
      // is stale — refetch so the next attempt sees the current list.
      if (message.toLowerCase().includes("variant")) {
        fetchVariants();
      }
      setSnackbar({ open: true, message, severity: "error" });
    }
  };

  const handleEditApp = async (formData: any) => {
    try {
      await uploadService.updateApp({ ...formData, app_id: request.editApp!.id });
      setSnackbar({
        open: true,
        message: "App updated successfully",
        severity: "success",
      });
      setUploadProgress(null);
      emitUploadComplete({ projectId: selectedProjectId, type: "app" });
      onClose();
    } catch (err) {
      console.error("Failed to update app:", err);
      setSnackbar({ open: true, message: "Failed to update app", severity: "error" });
    }
  };

  const handleTestBuildUploaded = (iteration: number) => {
    setSnackbar({
      open: true,
      message: `Test build uploaded as iteration #${iteration}`,
      severity: "success",
    });
    emitUploadComplete({ projectId: selectedProjectId, type: "testBuild" });
    onClose();
  };

  const goToVariants = () => {
    onClose();
    startTransition(() => navigate(`/dashboard/${selectedProjectId}?tab=variants`));
  };

  const title = request.editApp
    ? "Edit build"
    : request.addVersion
      ? "Add new version"
      : uploadType === "testBuild"
        ? "Upload test build"
        : uploadType === "app"
          ? "Upload build"
          : "Upload a build";

  return (
    <>
      <Drawer
        anchor="right"
        variant="temporary"
        open={open}
        onClose={busy ? undefined : onClose}
        slotProps={{
          paper: {
            sx: {
              width: { xs: "100%", sm: 480 },
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            },
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <IconButton onClick={onClose} disabled={busy} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>

        {request.file && (
          <Box>
            <Chip
              icon={<InsertDriveFileIcon />}
              label={`${request.file.name} (${formatSize(request.file.size)})`}
            />
          </Box>
        )}

        {showChooser && (
          <Stack spacing={3}>
            {!request.addVersion && !request.editApp && (
              <FormControl fullWidth required>
                <InputLabel id="upload-slideover-project-label" shrink>Project</InputLabel>
                <Select
                  labelId="upload-slideover-project-label"
                  label="Project"
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  displayEmpty
                  notched
                  disabled={projectsLoading}
                >
                  <MenuItem value="" disabled>
                    {projectsLoading ? "Loading projects…" : "Select a project"}
                  </MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {!selectedProjectId ? (
              <Alert severity="info">Select a project to continue.</Alert>
            ) : (
              !request.mode && (
                <Box>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
                    What are you uploading to <b>{selectedProject?.name}</b>?
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      onClick={() => setUploadType("app")}
                      sx={{ flex: 1, py: 1.5 }}
                    >
                      App Build
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<ScienceIcon />}
                      onClick={() => setUploadType("testBuild")}
                      sx={{
                        flex: 1,
                        py: 1.5,
                        color: "secondary.main",
                        borderColor: "secondary.main",
                      }}
                    >
                      Test Build
                    </Button>
                  </Stack>
                </Box>
              )
            )}

            <Box>
              <Button onClick={onClose}>Cancel</Button>
            </Box>
          </Stack>
        )}

        {!showChooser && uploadType === "app" && (
          <Box>
            <AppForm
              projectId={selectedProjectId}
              projects={projects}
              variants={variants}
              variantsLoading={variantsLoading}
              canManageVariants={canManageVariants}
              onManageVariants={goToVariants}
              item={request.editApp}
              addVersionMode={Boolean(request.addVersion)}
              lockedBuild={request.addVersion}
              replaceKeys={replaceKeys}
              initialFile={request.file ?? undefined}
              onConfirm={request.editApp ? handleEditApp : handleCreateApp}
              onClose={onClose}
              onProgress={setUploadProgress}
              onUploadFailed={() => setUploadProgress(null)}
            />
          </Box>
        )}

        {!showChooser && uploadType === "testBuild" && (
          <Box>
            <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 2 }}>
              Project: <b>{selectedProject?.name}</b>
            </Typography>
            <TestBuildUploadForm
              projectId={selectedProjectId}
              variants={variants}
              variantsLoading={variantsLoading}
              existingBuilds={existingTestBuilds}
              initialFile={request.file}
              onCancel={onClose}
              onUploaded={handleTestBuildUploaded}
              onUploadingChange={setTestBuildUploading}
            />
          </Box>
        )}
      </Drawer>

      {/* Outside the Drawer so a success message survives the close. */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UploadSlideOver;
